/**
 * traders.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Admin hired-traders management — session-gated, full sidebar, design-matched
 * to wallets.jsx / transactions.jsx / AdminDashboard.
 * Route: /admin/traders
 *
 * Tables used:
 *   trader_profiles    (id, user_id, bio, strategy, status, verified, created_at)
 *   trader_performance (trader_id, win_rate, total_trades, avg_return, total_pnl, follower_count)
 *   hire_requests       (id, client_id, capital_amount, currency, specialty, risk_appetite,
 *                         fee_preference, max_monthly_budget, notes, status, created_at)
 *   hire_agreements     (id, request_id, client_id, trader_id, capital_amount, currency,
 *                         duration_months, fee_structure, status, total_return, fees_paid,
 *                         started_at, ended_at)
 *   users               (id, display_name, email, status)
 *
 * Hire-request flow: a client submits an open `hire_requests` row describing what they
 * want (capital, specialty, risk appetite, fee preference) without naming a trader.
 * The admin reviews the request and assigns a trader, which creates a `hire_agreements`
 * row (request_id + client_id + trader_id) and closes out the request (status: 'fulfilled').
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import supabase from "../supabase";
import {
  // sidebar
  LayoutDashboard, Wallet, Receipt, Copy, UserCheck,
  HeadphonesIcon, Bell, CandlestickChart, BarChart2, ShoppingBag,
  ChevronRight, LogOut, Users,CreditCard, MapPin,
  // page
  Search, RefreshCw, Plus, X, AlertCircle,
  CheckCircle2, XCircle, Clock, Eye, Trash2,
  ChevronUp, ChevronDown, ShieldCheck, TrendingUp,
  Star, Briefcase, Filter, Menu, UserPlus,
} from "lucide-react";

const PER_PAGE = 12;

// ─── Sidebar nav ─────────────────────────────────────────────────────────────
const NAV_SECTIONS = [
  {
    label: "Overview",
    items: [{ to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true }],
  },
  {
    label: "Users & Finance",
    items: [
      { to: "/admin/users",        label: "Users",        icon: Users   },
      { to: "/admin/wallets",      label: "Wallets",      icon: Wallet  },
      { to: "/admin/transactions", label: "Transactions", icon: Receipt },
    ],
  },
  {
    label: "Trading",
    items: [
      { to: "/admin/copy-trades",  label: "Copy trades",   icon: Copy             },
      { to: "/admin/traders",      label: "Hired traders", icon: UserCheck        },
      { to: "/admin/trading",      label: "Open trades",   icon: CandlestickChart },
      { to: "/admin/stock-trades", label: "Stock trades",  icon: BarChart2        },
      { to: "/admin/marketplace",  label: "Marketplace",   icon: ShoppingBag      },
    ],
  },
  {
    label: "Communication",
    items: [
      { to: "/admin/support",       label: "Support",       icon: HeadphonesIcon },
      { to: "/admin/notifications", label: "Notifications", icon: Bell           },
    ],
  },
   {
    label: "Settings",
    items: [
      { to: "/admin/subscriptions",      label: "Subscriptions",      icon: CreditCard },
      { to: "/admin/payment-addresses",  label: "Payment Addresses",  icon: MapPin     },
      { to: "/admin/market-subscriptions",  label: "Market Subscriptions",  icon: MapPin     },
    ],
  },
];

// ─── Styles ───────────────────────────────────────────────────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg-base:      #06060f;
    --bg-surface:   #0a0a16;
    --bg-elevated:  #0e0e1c;
    --bg-card:      #111122;
    --bg-hover:     #14142a;
    --border:       rgba(255,255,255,0.055);
    --border-hi:    rgba(255,255,255,0.11);
    --border-glow:  rgba(99,102,241,0.3);
    --accent:       #6366f1;
    --accent-light: #818cf8;
    --accent-dim:   rgba(99,102,241,0.13);
    --accent-glow:  rgba(99,102,241,0.22);
    --green:        #34d399;
    --green-dim:    rgba(52,211,153,0.13);
    --amber:        #fbbf24;
    --amber-dim:    rgba(251,191,36,0.13);
    --rose:         #fb7185;
    --rose-dim:     rgba(251,113,133,0.13);
    --cyan:         #22d3ee;
    --cyan-dim:     rgba(34,211,238,0.13);
    --purple:       #a78bfa;
    --purple-dim:   rgba(167,139,250,0.13);
    --text-primary:   #eeeeff;
    --text-secondary: #7878a0;
    --text-muted:     #3e3e58;
    --font-sans: 'Inter', system-ui, sans-serif;
    --font-mono: 'JetBrains Mono', monospace;
    --sidebar-w: 232px;
    --topbar-h:  60px;
    --radius-sm: 8px;
    --radius-md: 14px;
    --radius-lg: 18px;
  }

  html, body, #root { height: 100%; background: var(--bg-base); font-family: var(--font-sans); color-scheme: dark; }

  /* Shell */
  .tr-shell {
    display: flex; height: 100vh; height: 100dvh; overflow: hidden;
    background: var(--bg-base); position: relative;
  }
  .tr-shell::before {
    content: ''; position: fixed; inset: 0; pointer-events: none; z-index: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E");
    opacity: 0.018;
  }

  /* ── Sidebar ── */
  .ad-sidebar {
    width: var(--sidebar-w); flex-shrink: 0;
    display: flex; flex-direction: column; height: 100%; overflow: hidden;
    background: var(--bg-surface); border-right: 1px solid var(--border);
    position: relative; z-index: 50;
    transition: transform .3s cubic-bezier(.4,0,.2,1); will-change: transform;
  }
  .ad-sidebar::after {
    content: ''; position: absolute; top: 0; right: 0; bottom: 0; width: 1px;
    background: linear-gradient(to bottom, transparent 0%, var(--accent-glow) 35%, rgba(99,102,241,0.08) 70%, transparent 100%);
    pointer-events: none;
  }
  .ad-logo {
    display: flex; align-items: center; gap: 11px;
    padding: 0 18px; height: var(--topbar-h);
    border-bottom: 1px solid var(--border); flex-shrink: 0;
  }
  .ad-logo-icon {
    width: 32px; height: 32px; border-radius: 10px;
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a78bfa 100%);
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    box-shadow: 0 0 0 1px rgba(99,102,241,0.4), 0 4px 16px rgba(99,102,241,0.35), 0 0 32px rgba(99,102,241,0.15);
  }
  .ad-logo-text strong {
    font-size: 13.5px; font-weight: 800; letter-spacing: -0.4px;
    background: linear-gradient(to right, #eeeeff, #a5b4fc);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
  }
  .ad-logo-text span { font-size: 9.5px; color: var(--text-muted); display: block; margin-top: 3px; letter-spacing: 0.8px; text-transform: uppercase; font-weight: 500; }
  .ad-nav { flex: 1; min-height: 0; overflow-y: auto; padding: 18px 10px; display: flex; flex-direction: column; gap: 22px; -webkit-overflow-scrolling: touch; scroll-behavior: smooth; }
  .ad-nav::-webkit-scrollbar { width: 3px; }
  .ad-nav::-webkit-scrollbar-track { background: transparent; }
  .ad-nav::-webkit-scrollbar-thumb { background: rgba(99,102,241,0.18); border-radius: 99px; }
  .ad-nav-section-label { font-size: 9px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--text-muted); padding: 0 10px; margin-bottom: 5px; }
  .ad-nav-items { display: flex; flex-direction: column; gap: 1px; }
  .ad-nav-link {
    display: flex; align-items: center; gap: 9px; padding: 8px 10px; border-radius: var(--radius-sm);
    font-size: 12.5px; font-weight: 500; color: var(--text-secondary);
    text-decoration: none; transition: all 0.18s ease; position: relative;
  }
  .ad-nav-link:hover { background: var(--bg-hover); color: var(--text-primary); }
  .ad-nav-link.active {
    background: linear-gradient(135deg, rgba(99,102,241,0.18) 0%, rgba(139,92,246,0.10) 100%);
    color: var(--accent-light); font-weight: 600;
    box-shadow: inset 0 0 0 1px rgba(99,102,241,0.15);
  }
  .ad-nav-link.active::before {
    content: ''; position: absolute; left: 0; top: 18%; bottom: 18%;
    width: 2.5px; border-radius: 99px;
    background: linear-gradient(to bottom, var(--accent), var(--purple));
    box-shadow: 0 0 10px var(--accent-glow), 0 0 20px rgba(99,102,241,0.2);
  }
  .ad-nav-link svg { flex-shrink: 0; opacity: 0.65; transition: opacity 0.15s; }
  .ad-nav-link:hover svg, .ad-nav-link.active svg { opacity: 1; }
  .ad-nav-chevron { margin-left: auto; opacity: 0.35; }
  .ad-user-card { flex-shrink: 0; border-top: 1px solid var(--border); padding: 12px 10px; background: linear-gradient(to top, rgba(99,102,241,0.04) 0%, transparent 100%); }
  .ad-user-inner { display: flex; align-items: center; gap: 10px; padding: 9px 10px; border-radius: var(--radius-sm); transition: background 0.15s; }
  .ad-user-inner:hover { background: var(--bg-hover); }
  .ad-user-avatar {
    width: 32px; height: 32px; border-radius: 50%;
    background: linear-gradient(135deg, var(--accent-dim), rgba(139,92,246,0.2));
    border: 1.5px solid rgba(99,102,241,0.35);
    display: flex; align-items: center; justify-content: center;
    font-size: 11px; font-weight: 700; color: var(--accent-light); flex-shrink: 0;
  }
  .ad-user-info { flex: 1; min-width: 0; }
  .ad-user-info strong { font-size: 12px; font-weight: 600; color: var(--text-primary); display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .ad-user-info span   { font-size: 10px; color: var(--text-muted); display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 1px; }
  .ad-logout-btn {
    width: 28px; height: 28px; border-radius: 7px; background: none; border: none; cursor: pointer;
    display: flex; align-items: center; justify-content: center; color: var(--text-muted); transition: all 0.15s;
  }
  .ad-logout-btn:hover { background: var(--rose-dim); color: var(--rose); }

  /* ── Page column ── */
  .tr-page { flex: 1; display: flex; flex-direction: column; overflow: hidden; position: relative; z-index: 1; min-width: 0; }

  /* Topbar */
  .tr-topbar {
    flex-shrink: 0; height: var(--topbar-h);
    background: rgba(10,10,22,0.85); backdrop-filter: blur(12px);
    border-bottom: 1px solid var(--border);
    display: flex; align-items: center; justify-content: space-between;
    padding: 0 28px; position: relative;
  }
  .tr-topbar::after {
    content: ''; position: absolute; bottom: -1px; left: 0; right: 0; height: 1px;
    background: linear-gradient(to right, transparent 0%, var(--accent-glow) 50%, transparent 100%);
  }
  .tr-topbar-left  { display: flex; align-items: center; gap: 14px; }
  .tr-topbar-right { display: flex; align-items: center; gap: 8px; }
  .tr-topbar-badge {
    display: flex; align-items: center; gap: 6px; padding: 3px 10px; border-radius: 99px;
    background: var(--accent-dim); border: 1px solid rgba(99,102,241,0.2);
    font-size: 10px; font-weight: 700; color: var(--accent-light); letter-spacing: 0.5px; text-transform: uppercase;
  }
  .tr-topbar-title { font-size: 15px; font-weight: 700; color: var(--text-primary); letter-spacing: -0.4px; }
  .tr-topbar-sub   { font-size: 11px; color: var(--text-muted); margin-top: 1px; }

  /* Buttons */
  .tr-btn {
    display: flex; align-items: center; gap: 6px; padding: 6px 14px; border-radius: var(--radius-sm);
    font-size: 11.5px; font-weight: 500; cursor: pointer; transition: all 0.18s; font-family: var(--font-sans);
    border: 1px solid var(--border-hi); background: var(--bg-card); color: var(--text-secondary);
  }
  .tr-btn:hover { background: var(--accent-dim); color: var(--accent-light); border-color: rgba(99,102,241,0.3); }
  .tr-btn-primary {
    display: flex; align-items: center; gap: 6px; padding: 6px 14px; border-radius: var(--radius-sm);
    font-size: 11.5px; font-weight: 600; cursor: pointer; transition: all 0.18s; font-family: var(--font-sans);
    border: none; background: var(--accent); color: #fff;
  }
  .tr-btn-primary:hover { background: #4f52e0; box-shadow: 0 0 20px rgba(99,102,241,0.35); }

  /* Main */
  .tr-main { flex: 1; overflow-y: auto; padding: 26px 28px; display: flex; flex-direction: column; gap: 20px; }
  .tr-main::-webkit-scrollbar { width: 4px; }
  .tr-main::-webkit-scrollbar-track { background: transparent; }
  .tr-main::-webkit-scrollbar-thumb { background: rgba(99,102,241,0.25); border-radius: 99px; }

  /* Stat grid */
  .tr-stat-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }
  .tr-stat {
    background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-md); padding: 18px 20px;
    display: flex; flex-direction: column; gap: 12px; position: relative; overflow: hidden;
    transition: border-color 0.22s, box-shadow 0.22s, transform 0.18s;
  }
  .tr-stat::before {
    content: ''; position: absolute; inset: 0;
    background: radial-gradient(ellipse at 110% -10%, var(--stat-glow, transparent) 0%, transparent 60%);
    pointer-events: none;
  }
  .tr-stat::after {
    content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 1px;
    background: linear-gradient(to right, transparent 20%, var(--stat-bar, transparent) 50%, transparent 80%);
    pointer-events: none;
  }
  .tr-stat:hover { border-color: rgba(255,255,255,0.1); box-shadow: 0 4px 24px rgba(0,0,0,0.4); transform: translateY(-1px); }
  .tr-stat-top   { display: flex; align-items: flex-start; justify-content: space-between; }
  .tr-stat-label { font-size: 9.5px; font-weight: 700; letter-spacing: 1.1px; text-transform: uppercase; color: var(--text-muted); }
  .tr-stat-icon  { width: 32px; height: 32px; border-radius: 9px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .tr-stat-value { font-size: 26px; font-weight: 700; color: var(--text-primary); letter-spacing: -1.5px; font-family: var(--font-mono); line-height: 1; }
  .tr-stat-sub   { font-size: 10.5px; color: var(--text-muted); margin-top: 4px; }

  /* Tabs */
  .tr-tabs { display: flex; gap: 2px; padding: 4px; background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-md); width: fit-content; }
  .tr-tab {
    display: flex; align-items: center; gap: 7px; padding: 7px 16px; border-radius: 10px;
    font-size: 12.5px; font-weight: 500; cursor: pointer; transition: all 0.18s;
    background: none; border: none; color: var(--text-muted); font-family: var(--font-sans);
  }
  .tr-tab:hover { color: var(--text-secondary); }
  .tr-tab.active { background: linear-gradient(135deg, rgba(99,102,241,0.2), rgba(139,92,246,0.12)); color: var(--accent-light); font-weight: 600; box-shadow: inset 0 0 0 1px rgba(99,102,241,0.2); }
  .tr-tab-badge {
    padding: 1px 7px; border-radius: 99px; font-size: 10px; font-weight: 700;
    font-family: var(--font-mono);
  }
  .tr-tab-badge.normal { background: rgba(255,255,255,0.07); color: var(--text-muted); }
  .tr-tab-badge.alert  { background: var(--amber); color: #0a0a0a; }

  /* Pending banner */
  .tr-pending-banner {
    display: flex; align-items: center; gap: 12px; padding: 12px 18px; border-radius: var(--radius-sm);
    background: var(--amber-dim); border: 1px solid rgba(251,191,36,0.22);
  }
  .tr-pending-banner p { font-size: 12.5px; color: var(--amber); flex: 1; }
  .tr-pending-banner strong { font-weight: 700; }
  .tr-pending-link {
    font-size: 11px; color: var(--amber); background: none; border: none;
    cursor: pointer; text-decoration: underline; font-family: var(--font-sans); white-space: nowrap;
  }
  .tr-pending-link:hover { text-decoration: none; }

  /* Panel */
  .tr-panel { background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-md); overflow: hidden; }
  .tr-panel-amber { background: var(--bg-card); border: 1px solid rgba(251,191,36,0.2); border-radius: var(--radius-md); overflow: hidden; }

  /* Toolbar */
  .tr-toolbar {
    display: flex; align-items: center; justify-content: space-between;
    gap: 12px; padding: 14px 20px; border-bottom: 1px solid var(--border); flex-wrap: wrap;
  }
  .tr-toolbar-left  { display: flex; align-items: center; gap: 10px; }
  .tr-toolbar-right { display: flex; align-items: center; gap: 8px; }
  .tr-panel-title { font-size: 13px; font-weight: 700; color: var(--text-primary); }
  .tr-count-pill { padding: 2px 8px; border-radius: 99px; border: 1px solid var(--border-hi); font-size: 10px; color: var(--text-muted); font-family: var(--font-mono); }

  .tr-select {
    padding: 5px 10px; background: var(--bg-surface); border: 1px solid var(--border);
    border-radius: var(--radius-sm); font-size: 11.5px; color: var(--text-primary);
    font-family: var(--font-sans); outline: none; transition: border-color 0.18s; cursor: pointer;
  }
  .tr-select:focus { border-color: rgba(99,102,241,0.4); }
  .tr-search-wrap { position: relative; }
  .tr-search-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: var(--text-muted); pointer-events: none; }
  .tr-search {
    padding: 6px 12px 6px 30px; background: var(--bg-surface); border: 1px solid var(--border);
    border-radius: var(--radius-sm); font-size: 12px; color: var(--text-primary);
    font-family: var(--font-sans); width: 200px; outline: none; transition: border-color 0.18s;
  }
  .tr-search::placeholder { color: var(--text-muted); }
  .tr-search:focus { border-color: rgba(99,102,241,0.4); box-shadow: 0 0 0 3px rgba(99,102,241,0.08); }

  /* Table */
  .tr-table-wrap { overflow-x: auto; }
  .tr-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .tr-table thead tr { border-bottom: 1px solid var(--border); }
  .tr-table th {
    padding: 10px 18px; text-align: left; font-size: 9.5px; font-weight: 700;
    letter-spacing: 1.1px; text-transform: uppercase; color: var(--text-muted);
    white-space: nowrap; cursor: pointer; user-select: none; transition: color 0.15s;
  }
  .tr-table th:hover { color: var(--text-secondary); }
  .tr-table th.no-sort { cursor: default; }
  .tr-table tbody tr { border-bottom: 1px solid rgba(255,255,255,0.035); transition: background 0.12s; }
  .tr-table tbody tr:last-child { border-bottom: none; }
  .tr-table tbody tr:hover { background: rgba(99,102,241,0.04); }
  .tr-table td { padding: 13px 18px; vertical-align: middle; }

  /* Avatar cell */
  .tr-avatar-cell { display: flex; align-items: center; gap: 10px; }
  .tr-avatar {
    width: 34px; height: 34px; border-radius: 50%; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    font-size: 11px; font-weight: 700; border: 1.5px solid rgba(99,102,241,0.2);
  }
  .tr-avatar-indigo { background: rgba(99,102,241,0.15);  color: #818cf8; }
  .tr-avatar-green  { background: rgba(52,211,153,0.15);  color: #34d399; }
  .tr-avatar-amber  { background: rgba(251,191,36,0.15);  color: #fbbf24; }
  .tr-avatar-rose   { background: rgba(251,113,133,0.15); color: #fb7185; }
  .tr-avatar-purple { background: rgba(167,139,250,0.15); color: #a78bfa; }
  .tr-cell-name  { font-size: 12.5px; font-weight: 500; color: var(--text-primary); }
  .tr-cell-email { font-size: 10.5px; color: var(--text-muted); margin-top: 1px; }
  .tr-date { font-size: 11px; color: var(--text-muted); font-family: var(--font-mono); white-space: nowrap; }

  /* Chips */
  .tr-chip {
    display: inline-flex; align-items: center; gap: 5px; padding: 3px 9px; border-radius: 99px;
    font-size: 10px; font-weight: 700; letter-spacing: 0.4px; text-transform: capitalize; border: 1px solid;
  }
  .tr-chip.active   { background: var(--green-dim); color: var(--green); border-color: rgba(52,211,153,0.25); }
  .tr-chip.inactive { background: rgba(255,255,255,0.05); color: var(--text-muted); border-color: var(--border); }
  .tr-chip.pending  { background: var(--amber-dim); color: var(--amber); border-color: rgba(251,191,36,0.25); }
  .tr-chip.approved { background: var(--green-dim); color: var(--green); border-color: rgba(52,211,153,0.25); }
  .tr-chip.rejected { background: var(--rose-dim);  color: var(--rose);  border-color: rgba(251,113,133,0.25); }
  .tr-chip.open      { background: var(--amber-dim); color: var(--amber); border-color: rgba(251,191,36,0.25); }
  .tr-chip.fulfilled { background: var(--green-dim); color: var(--green); border-color: rgba(52,211,153,0.25); }
  .tr-chip.cancelled { background: var(--rose-dim);  color: var(--rose);  border-color: rgba(251,113,133,0.25); }

  .tr-verified {
    display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: 99px;
    font-size: 10px; font-weight: 600; color: var(--accent-light);
    background: var(--accent-dim); border: 1px solid rgba(99,102,241,0.25);
  }

  /* Strategy preview */
  .tr-strategy { font-size: 11.5px; color: var(--text-secondary); max-width: 180px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

  /* Action buttons */
  .tr-actions { display: flex; align-items: center; justify-content: flex-end; gap: 4px; }
  .tr-icon-btn {
    width: 28px; height: 28px; border-radius: 7px; display: flex; align-items: center; justify-content: center;
    background: none; border: 1px solid var(--border); color: var(--text-muted);
    cursor: pointer; transition: all 0.15s; flex-shrink: 0;
  }
  .tr-icon-btn:hover        { background: var(--bg-hover); color: var(--text-primary); border-color: var(--border-hi); }
  .tr-icon-btn.danger:hover { background: var(--rose-dim); color: var(--rose); border-color: rgba(251,113,133,0.3); }

  /* Skeleton */
  .tr-skel {
    height: 10px; border-radius: 5px;
    background: linear-gradient(90deg, var(--bg-hover) 25%, rgba(99,102,241,0.05) 50%, var(--bg-hover) 75%);
    background-size: 200% 100%; animation: tr-shimmer 1.8s infinite;
  }
  @keyframes tr-shimmer { 0%{ background-position:200% 0 } 100%{ background-position:-200% 0 } }

  /* Empty */
  .tr-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 56px; gap: 8px; color: var(--text-muted); font-size: 12px; }

  /* Pagination */
  .tr-pagination { display: flex; align-items: center; justify-content: space-between; padding: 12px 20px; border-top: 1px solid var(--border); }
  .tr-pag-info  { font-size: 11px; color: var(--text-muted); font-family: var(--font-mono); }
  .tr-pag-pages { display: flex; gap: 4px; }
  .tr-pag-btn {
    width: 28px; height: 28px; border-radius: 7px; display: flex; align-items: center; justify-content: center;
    font-size: 11px; font-family: var(--font-mono); border: 1px solid var(--border);
    background: none; color: var(--text-muted); cursor: pointer; transition: all 0.15s;
  }
  .tr-pag-btn:hover  { background: var(--bg-hover); color: var(--text-primary); }
  .tr-pag-btn.active { background: var(--accent-dim); color: var(--accent-light); border-color: rgba(99,102,241,0.3); }

  /* Request rows */
  .tr-req-row {
    display: flex; align-items: flex-start; gap: 14px; padding: 16px 20px;
    border-bottom: 1px solid rgba(255,255,255,0.035); transition: background 0.12s;
  }
  .tr-req-row:last-child { border-bottom: none; }
  .tr-req-row:hover { background: rgba(99,102,241,0.03); }
  .tr-req-body { flex: 1; min-width: 0; }
  .tr-req-names { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; margin-bottom: 3px; }
  .tr-req-client { font-size: 13px; font-weight: 600; color: var(--text-primary); }
  .tr-req-verb   { font-size: 11px; color: var(--text-muted); }
  .tr-req-trader { font-size: 13px; font-weight: 600; color: var(--accent-light); }
  .tr-req-criteria { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; font-size: 11px; color: var(--text-secondary); margin-bottom: 3px; font-family: var(--font-mono); }
  .tr-req-dot { color: var(--text-muted); }
  .tr-req-msg    { font-size: 11.5px; color: var(--text-secondary); margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 400px; }
  .tr-req-date   { font-size: 10.5px; color: var(--text-muted); margin-top: 4px; font-family: var(--font-mono); }
  .tr-req-actions { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
  .tr-approve-inline {
    display: flex; align-items: center; gap: 5px; padding: 5px 12px; border-radius: var(--radius-sm);
    font-size: 11.5px; font-weight: 600; cursor: pointer; transition: all 0.15s; font-family: var(--font-sans);
    border: 1px solid rgba(52,211,153,0.25); background: var(--green-dim); color: var(--green);
  }
  .tr-approve-inline:hover:not(:disabled) { background: rgba(52,211,153,0.22); }
  .tr-approve-inline:disabled { opacity: 0.4; cursor: not-allowed; }
  .tr-reject-inline {
    display: flex; align-items: center; gap: 5px; padding: 5px 12px; border-radius: var(--radius-sm);
    font-size: 11.5px; font-weight: 600; cursor: pointer; transition: all 0.15s; font-family: var(--font-sans);
    border: 1px solid rgba(251,113,133,0.25); background: var(--rose-dim); color: var(--rose);
  }
  .tr-reject-inline:hover:not(:disabled) { background: rgba(251,113,133,0.22); }
  .tr-reject-inline:disabled { opacity: 0.4; cursor: not-allowed; }
  .tr-assign-inline {
    display: flex; align-items: center; gap: 4px; padding: 6px 10px; border-radius: var(--radius-sm);
    background: var(--accent-dim); color: var(--accent-light); border: 1px solid rgba(99,102,241,0.25);
    font-size: 11px; font-weight: 600; cursor: pointer; transition: background .15s ease;
  }
  .tr-assign-inline:hover:not(:disabled) { background: rgba(99,102,241,0.25); }
  .tr-assign-inline:disabled { opacity: 0.4; cursor: not-allowed; }
  .tr-req-unassigned { font-size: 10.5px; color: var(--amber); font-weight: 600; display: inline-flex; align-items: center; gap: 4px; }
  .tr-assign-option {
    display: flex; align-items: center; gap: 10px; padding: 10px 12px; border-radius: var(--radius-sm);
    border: 1px solid var(--border); cursor: pointer; transition: all .15s ease;
  }
  .tr-assign-option:hover { background: var(--bg-hover); border-color: var(--border-hi); }
  .tr-assign-option.selected { background: var(--accent-dim); border-color: rgba(99,102,241,0.4); }
  .tr-assign-list { display: flex; flex-direction: column; gap: 6px; max-height: 320px; overflow-y: auto; }

  /* Modal */
  .tr-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,0.72); backdrop-filter: blur(4px);
    display: flex; align-items: center; justify-content: center; z-index: 50; padding: 16px;
  }
  .tr-modal {
    background: var(--bg-elevated); border: 1px solid var(--border-hi); border-radius: var(--radius-lg);
    width: 100%; max-width: 480px; display: flex; flex-direction: column;
    box-shadow: 0 24px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(99,102,241,0.1);
  }
  .tr-modal-sm { max-width: 380px; }
  .tr-modal-head {
    display: flex; align-items: flex-start; justify-content: space-between;
    padding: 20px 24px; border-bottom: 1px solid var(--border); flex-shrink: 0;
  }
  .tr-modal-title { font-size: 15px; font-weight: 700; color: var(--text-primary); letter-spacing: -0.3px; }
  .tr-modal-sub   { font-size: 12px; color: var(--text-secondary); margin-top: 3px; }
  .tr-modal-body  { padding: 22px 24px; display: flex; flex-direction: column; gap: 16px; overflow-y: auto; max-height: 75vh; }
  .tr-modal-foot  { display: flex; align-items: center; justify-content: flex-end; gap: 8px; padding: 16px 24px; border-top: 1px solid var(--border); }
  .tr-modal-foot-split { display: flex; align-items: center; justify-content: space-between; padding: 16px 24px; border-top: 1px solid var(--border); }
  .tr-close-btn {
    width: 28px; height: 28px; border-radius: 7px; background: none; border: 1px solid var(--border);
    display: flex; align-items: center; justify-content: center; color: var(--text-muted); cursor: pointer; transition: all 0.15s; flex-shrink: 0;
  }
  .tr-close-btn:hover { background: var(--rose-dim); color: var(--rose); border-color: rgba(251,113,133,0.25); }

  /* Detail grid */
  .tr-perf-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
  .tr-perf-cell { padding: 12px 14px; border-radius: var(--radius-sm); background: rgba(255,255,255,0.025); border: 1px solid var(--border); }
  .tr-perf-label { font-size: 9.5px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: var(--text-muted); margin-bottom: 6px; }
  .tr-perf-val   { font-size: 16px; font-weight: 700; color: var(--text-primary); font-family: var(--font-mono); }
  .tr-detail-hr  { border: none; border-top: 1px solid var(--border); }
  .tr-user-row   { display: flex; align-items: center; gap: 12px; padding: 12px 16px; border-radius: var(--radius-sm); background: rgba(255,255,255,0.025); border: 1px solid var(--border); }
  .tr-text-block { padding: 10px 14px; border-radius: var(--radius-sm); background: rgba(255,255,255,0.025); border: 1px solid var(--border); font-size: 12px; color: var(--text-secondary); line-height: 1.6; }
  .tr-section-label { font-size: 9.5px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: var(--text-muted); margin-bottom: 8px; }

  /* Form */
  .tr-label { font-size: 11px; font-weight: 600; color: var(--text-secondary); margin-bottom: 6px; display: block; }
  .tr-input {
    width: 100%; padding: 9px 12px; background: var(--bg-surface); border: 1px solid var(--border);
    border-radius: var(--radius-sm); font-size: 13px; color: var(--text-primary);
    font-family: var(--font-sans); outline: none; transition: border-color 0.18s; resize: vertical;
  }
  .tr-input::placeholder { color: var(--text-muted); }
  .tr-input:focus { border-color: rgba(99,102,241,0.45); box-shadow: 0 0 0 3px rgba(99,102,241,0.08); }
  .tr-select-full {
    width: 100%; padding: 9px 12px; background: var(--bg-surface); border: 1px solid var(--border);
    border-radius: var(--radius-sm); font-size: 13px; color: var(--text-primary);
    font-family: var(--font-sans); outline: none; transition: border-color 0.18s; cursor: pointer;
  }
  .tr-select-full:focus { border-color: rgba(99,102,241,0.45); }
  .tr-toggle-row { display: flex; align-items: center; gap: 12px; }
  .tr-toggle { width: 36px; height: 20px; border-radius: 99px; border: none; cursor: pointer; position: relative; transition: background 0.2s; flex-shrink: 0; }
  .tr-toggle.on  { background: var(--accent); }
  .tr-toggle.off { background: rgba(255,255,255,0.1); }
  .tr-toggle-thumb {
    position: absolute; top: 2px; width: 16px; height: 16px;
    border-radius: 50%; background: #fff; transition: left 0.2s;
  }
  .tr-toggle.on  .tr-toggle-thumb { left: 18px; }
  .tr-toggle.off .tr-toggle-thumb { left: 2px; }
  .tr-toggle-label { font-size: 13px; color: var(--text-secondary); }

  /* Error */
  .tr-error {
    display: flex; align-items: center; gap: 8px; padding: 10px 14px; border-radius: var(--radius-sm);
    background: rgba(251,113,133,0.08); border: 1px solid rgba(251,113,133,0.22); color: var(--rose); font-size: 12px;
  }

  /* Ghost / submit buttons */
  .tr-ghost-btn {
    display: flex; align-items: center; gap: 6px; padding: 7px 16px; border-radius: var(--radius-sm);
    background: none; border: 1px solid var(--border-hi); color: var(--text-secondary);
    font-size: 12px; font-weight: 500; cursor: pointer; transition: all 0.15s; font-family: var(--font-sans);
  }
  .tr-ghost-btn:hover { background: var(--bg-hover); color: var(--text-primary); }
  .tr-submit-btn {
    display: flex; align-items: center; gap: 6px; padding: 7px 18px; border-radius: var(--radius-sm);
    font-size: 12px; font-weight: 600; cursor: pointer; transition: all 0.15s; font-family: var(--font-sans);
    border: none; background: var(--accent); color: #fff;
  }
  .tr-submit-btn:disabled { opacity: 0.4; cursor: not-allowed; }
  .tr-submit-btn:not(:disabled):hover { background: #4f52e0; box-shadow: 0 0 16px rgba(99,102,241,0.4); }
  .tr-danger-btn {
    display: flex; align-items: center; gap: 6px; padding: 7px 16px; border-radius: var(--radius-sm);
    font-size: 12px; font-weight: 600; cursor: pointer; transition: all 0.15s; font-family: var(--font-sans);
    border: none; background: var(--rose-dim); color: var(--rose); border: 1px solid rgba(251,113,133,0.25);
  }
  .tr-danger-btn:not(:disabled):hover { background: rgba(251,113,133,0.2); }
  .tr-danger-btn:disabled { opacity: 0.4; cursor: not-allowed; }

  /* Confirm modal */
  .tr-confirm-icon { width: 40px; height: 40px; border-radius: 50%; background: var(--rose-dim); border: 1px solid rgba(251,113,133,0.25); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }

  /* Toast */
  .tr-toast {
    position: fixed; bottom: 24px; right: 24px; z-index: 100;
    display: flex; align-items: center; gap: 10px; padding: 12px 18px;
    border-radius: var(--radius-md); background: var(--bg-elevated);
    border: 1px solid var(--border-hi); box-shadow: 0 8px 32px rgba(0,0,0,0.5);
    font-size: 13px; color: var(--text-primary); animation: tr-slide-in 0.22s ease;
  }
  .tr-toast.ok  { border-color: rgba(52,211,153,0.35); }
  .tr-toast.err { border-color: rgba(251,113,133,0.35); }
  @keyframes tr-slide-in { from { opacity:0; transform:translateY(8px) } to { opacity:1; transform:translateY(0) } }

  /* Overlay */
  .tr-overlay-mob { display: none; position: fixed; inset: 0; z-index: 40; background: rgba(0,0,0,.6); backdrop-filter: blur(2px); opacity: 0; transition: opacity .28s ease; pointer-events: none; }
  .tr-overlay-mob.open { display: block; opacity: 1; pointer-events: auto; }

  /* Sidebar close button (mobile) */
  .ad-logo-close { display: none; margin-left: auto; width: 28px; height: 28px; border-radius: 7px; background: none; border: none; cursor: pointer; align-items: center; justify-content: center; color: var(--text-muted); transition: all .15s; flex-shrink: 0; }
  .ad-logo-close:hover { background: var(--rose-dim); color: var(--rose); }

  /* Hamburger */
  .tr-hamburger { display: none; width: 36px; height: 36px; border-radius: var(--radius-sm); background: var(--bg-card); border: 1px solid var(--border); cursor: pointer; align-items: center; justify-content: center; color: var(--text-secondary); transition: all .15s; flex-shrink: 0; }
  .tr-hamburger:hover { background: var(--accent-dim); color: var(--accent-light); border-color: rgba(99,102,241,.25); }

  /* Responsive */
  @media(min-width:1400px) { :root { --sidebar-w: 252px; } .tr-main { padding: 30px 36px; } .tr-topbar { padding: 0 36px; } }
  @media(max-width:1280px) { .tr-stat-grid { grid-template-columns: repeat(2,1fr); } }
  @media(max-width:1023px) { :root { --sidebar-w: 220px; } .tr-main { padding: 20px; gap: 16px; } .tr-topbar { padding: 0 16px; } }
  @media(max-width:767px) {
    :root { --topbar-h: 56px; }
    .tr-hamburger { display: flex; }
    .ad-logo-close { display: flex; }
    .ad-sidebar { position: fixed; top: 0; left: 0; bottom: 0; height: 100%; height: 100dvh; overflow: hidden; transform: translateX(-100%); box-shadow: 4px 0 32px rgba(0,0,0,.6); transition: transform .3s cubic-bezier(.4,0,.2,1); z-index: 50; }
    .ad-sidebar.open { transform: translateX(0); }
    .tr-topbar { padding: 0 14px; }
    .tr-topbar-sub { display: none; }
    .tr-main { padding: 14px; gap: 14px; }
    .tr-stat-grid { grid-template-columns: repeat(2,1fr); gap: 10px; }
    .tr-toolbar { flex-direction: column; align-items: stretch; }
    .tr-toolbar-right { flex-wrap: wrap; }
    .tr-search { width: 100%; }
    .tr-toast { left: 14px; right: 14px; bottom: 14px; }
    .tr-topbar-badge { display: none; }
  }
  @media(max-width:479px) {
    .tr-stat-grid { grid-template-columns: 1fr 1fr; gap: 8px; }
    .tr-stat { padding: 14px 12px; }
    .tr-stat-value { font-size: 22px; }
  }

  /* Gate */
  .tr-gate { height: 100vh; display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 14px; background: var(--bg-base); }
  .tr-gate-icon { width: 36px; height: 36px; border-radius: 10px; background: linear-gradient(135deg,#6366f1,#a78bfa); box-shadow: 0 0 24px rgba(99,102,241,0.4); display: flex; align-items: center; justify-content: center; }
`;

function StyleInjector() {
  useEffect(() => {
    const el = document.createElement("style");
    el.textContent = STYLES;
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, []);
  return null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtDate(str) {
  if (!str) return "—";
  return new Date(str).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}
function fmtDateTime(str) {
  if (!str) return "—";
  return new Date(str).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
function fmtCurrency(val) {
  if (val == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(val);
}
function fmtAmount(val) {
  if (val == null) return "—";
  return new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val);
}
function initials(displayName) {
  const parts = (displayName ?? "").trim().split(/\s+/);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return (parts[0]?.[0] ?? "?").toUpperCase();
}
const PALETTE = ["indigo", "green", "amber", "rose", "purple"];
function avatarColor(id) {
  const n = typeof id === "number" ? id : (id ? id.charCodeAt(0) : 0);
  return PALETTE[n % PALETTE.length];
}

// ─── Sidebar link ─────────────────────────────────────────────────────────────
function SidebarLink({ to, label, icon: Icon, end, onClick }) {
  return (
    <NavLink to={to} end={end} onClick={onClick} className={({ isActive }) => `ad-nav-link${isActive ? " active" : ""}`}>
      {({ isActive }) => (
        <>
          <Icon size={14} />
          <span style={{ flex: 1 }}>{label}</span>
          {isActive && <ChevronRight size={10} className="ad-nav-chevron" />}
        </>
      )}
    </NavLink>
  );
}

function SortIcon({ field, sortField, sortDir }) {
  if (field !== sortField) return <ChevronUp size={9} style={{ opacity: 0.2, marginLeft: 4, display: "inline" }} />;
  return sortDir === 1
    ? <ChevronUp   size={9} style={{ color: "var(--accent-light)", marginLeft: 4, display: "inline" }} />
    : <ChevronDown size={9} style={{ color: "var(--accent-light)", marginLeft: 4, display: "inline" }} />;
}

function StatusChip({ status }) {
  const icons = {
    active: CheckCircle2, inactive: XCircle, pending: Clock,
    approved: CheckCircle2, rejected: XCircle,
    open: Clock, fulfilled: CheckCircle2, cancelled: XCircle,
  };
  const Icon = icons[status] ?? Clock;
  return <span className={`tr-chip ${status}`}><Icon size={9} />{status}</span>;
}

// ─── Trader detail modal ──────────────────────────────────────────────────────
function TraderDetailModal({ trader, onClose, onDelete }) {
  const [perf,    setPerf]    = useState(null);
  const [loading, setLoading] = useState(true);
  const aColor = avatarColor(trader.id);

  useEffect(() => {
    supabase.from("trader_performance").select("*").eq("trader_id", trader.id).maybeSingle()
      .then(({ data }) => { setPerf(data); setLoading(false); });
  }, [trader.id]);

  return (
    <div className="tr-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="tr-modal">
        <div className="tr-modal-head">
          <div>
            <div className="tr-modal-title">Trader profile</div>
            <div className="tr-modal-sub">Profile ID #{trader.id}</div>
          </div>
          <button className="tr-close-btn" onClick={onClose}><X size={13} /></button>
        </div>

        <div className="tr-modal-body">
          {/* Identity */}
          <div className="tr-user-row">
            <div className={`tr-avatar tr-avatar-${aColor}`} style={{ width: 40, height: 40, fontSize: 13 }}>
              {initials(trader.display_name)}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text-primary)" }}>{trader.display_name}</div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1 }}>@{trader.handle}</div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{trader.email}</div>
              {(trader.phone || trader.country) && (
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1 }}>
                  {[trader.phone, trader.country].filter(Boolean).join(" · ")}
                </div>
              )}
              <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
                <StatusChip status={trader.is_active ? "active" : "inactive"} />
                {trader.is_verified && (
                  <span className="tr-verified"><ShieldCheck size={9} />Verified</span>
                )}
                {trader.badge_label && (
                  <span className="tr-verified" style={{ background: "rgba(245,158,11,0.15)", color: "#f59e0b", borderColor: "rgba(245,158,11,0.3)" }}>
                    <Star size={9} />{trader.badge_label}
                  </span>
                )}
              </div>
            </div>
          </div>

          <hr className="tr-detail-hr" />

          {/* Profile details */}
          <div>
            <div className="tr-section-label">Profile details</div>
            <div className="tr-perf-grid">
              {[
                ["Market",       trader.market        ?? "—"],
                ["Risk level",   trader.risk_level    ?? "—"],
                ["Fee type",     trader.fee_type      ?? "—"],
                ["Fee structure",trader.fee_structure ?? "—"],
                ["Min capital",  trader.min_capital   != null ? fmtCurrency(trader.min_capital) : "—"],
                ["Years active", trader.years_active  != null ? `${trader.years_active} yr${trader.years_active !== 1 ? "s" : ""}` : "—"],
                ["Total copiers",trader.total_copiers ?? "—"],
                ["Total clients",trader.total_clients ?? "—"],
                ["Joined",       fmtDate(trader.created_at)],
              ].map(([label, val]) => (
                <div key={label} className="tr-perf-cell">
                  <div className="tr-perf-label">{label}</div>
                  <div className="tr-perf-val">{val}</div>
                </div>
              ))}
            </div>
          </div>

          {(trader.bio || trader.strategy_desc || trader.strategy) && <hr className="tr-detail-hr" />}

          {trader.bio && (
            <div>
              <div className="tr-section-label">Bio</div>
              <div className="tr-text-block">{trader.bio}</div>
            </div>
          )}
          {(trader.strategy_desc || trader.strategy) && (
            <div>
              <div className="tr-section-label">Strategy</div>
              <div className="tr-text-block">{trader.strategy_desc ?? trader.strategy}</div>
            </div>
          )}

          <hr className="tr-detail-hr" />

          {/* Performance */}
          <div>
            <div className="tr-section-label">Performance</div>
            {loading ? (
              <div className="tr-perf-grid">
                {[1,2,3,4,5,6].map((i) => <div key={i} className="tr-perf-cell"><div className="tr-skel" style={{ width: "60%", marginBottom: 8 }} /><div className="tr-skel" style={{ width: "80%" }} /></div>)}
              </div>
            ) : !perf ? (
              <div style={{ fontSize: 12, color: "var(--text-muted)", padding: "8px 0" }}>No performance data yet.</div>
            ) : (
              <div className="tr-perf-grid">
                {[
                  ["Win rate",     perf.win_rate    != null ? `${perf.win_rate}%`   : "—"],
                  ["Total trades", perf.total_trades ?? "—"],
                  ["Avg return",   perf.avg_return  != null ? `${perf.avg_return}%` : "—"],
                  ["Total P&L",    fmtCurrency(perf.total_pnl)],
                  ["Followers",    perf.follower_count ?? "—"],
                ].map(([label, val]) => (
                  <div key={label} className="tr-perf-cell">
                    <div className="tr-perf-label">{label}</div>
                    <div className="tr-perf-val">{val}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="tr-modal-foot-split">
          <button className="tr-danger-btn" onClick={() => { onDelete(trader); onClose(); }}>
            <Trash2 size={12} />Remove trader
          </button>
          <button className="tr-ghost-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── Add trader modal ─────────────────────────────────────────────────────────
function AddTraderModal({ onClose, onSuccess }) {
  // Personal
  const [displayName,   setDisplayName]   = useState("");
  const [handle,        setHandle]        = useState("");
  const [email,         setEmail]         = useState("");
  const [phone,         setPhone]         = useState("");
  const [country,       setCountry]       = useState("");
  // Profile
  const [market,        setMarket]        = useState("forex");
  const [riskLevel,     setRiskLevel]     = useState("medium");
  const [feeType,       setFeeType]       = useState("percentage");
  const [feeStructure,  setFeeStructure]  = useState("");
  const [minCapital,    setMinCapital]    = useState("");
  const [yearsActive,   setYearsActive]   = useState("0");
  const [bio,           setBio]           = useState("");
  const [strategyDesc,  setStrategyDesc]  = useState("");
  const [isVerified,    setIsVerified]    = useState(false);
  // UI
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState("");

  async function handleSubmit() {
    setError("");
    if (!displayName.trim()) { setError("Display name is required."); return; }
    if (!handle.trim())      { setError("Handle is required.");       return; }
    if (!email.trim())       { setError("Email is required.");        return; }
    setLoading(true);
    try {
      const { error: profileErr } = await supabase
        .from("trader_profiles")
        .insert({
          display_name:  displayName.trim(),
          handle:        handle.trim().toLowerCase().replace(/\s+/g, "_"),
          email:         email.trim().toLowerCase(),
          phone:         phone.trim()        || null,
          country:       country.trim()      || null,
          market,
          risk_level:    riskLevel,
          fee_type:      feeType,
          fee_structure: feeStructure.trim() || null,
          min_capital:   minCapital          ? parseFloat(minCapital) : null,
          years_active:  parseInt(yearsActive, 10) || 0,
          bio:           bio.trim()          || null,
          strategy_desc: strategyDesc.trim() || null,
          is_verified:   isVerified,
          is_active:     true,
          status:        "active",
        });
      if (profileErr) throw profileErr;
      onSuccess("Trader created successfully.");
      onClose();
    } catch (e) {
      setError(e.message ?? "Failed to create trader.");
    } finally {
      setLoading(false);
    }
  }

  const req = <span style={{ color: "var(--rose)" }}>*</span>;
  const opt = <span style={{ color: "var(--text-muted)", fontWeight: 400 }}> (optional)</span>;

  const sectionLabel = {
    fontSize: 10, fontWeight: 700, letterSpacing: "1.2px",
    textTransform: "uppercase", color: "var(--text-muted)",
    paddingBottom: 2, borderBottom: "1px solid var(--border)", marginTop: 4,
  };

  return (
    <div className="tr-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="tr-modal" style={{ maxWidth: 540 }}>
        <div className="tr-modal-head">
          <div>
            <div className="tr-modal-title">Add new trader</div>
            <div className="tr-modal-sub">Creates a standalone trader profile</div>
          </div>
          <button className="tr-close-btn" onClick={onClose}><X size={13} /></button>
        </div>

        <div className="tr-modal-body">

          {/* ── Personal info ── */}
          <div style={sectionLabel}>Personal information</div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label className="tr-label">Display name {req}</label>
              <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g. James Carter" className="tr-input" />
            </div>
            <div>
              <label className="tr-label">Handle {req}</label>
              <input type="text" value={handle} onChange={(e) => setHandle(e.target.value)}
                placeholder="e.g. jamescarter" className="tr-input" />
            </div>
          </div>

          <div>
            <label className="tr-label">Email address {req}</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="trader@example.com" className="tr-input" />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label className="tr-label">Phone {opt}</label>
              <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 555 000 0000" className="tr-input" />
            </div>
            <div>
              <label className="tr-label">Country {opt}</label>
              <input type="text" value={country} onChange={(e) => setCountry(e.target.value)}
                placeholder="e.g. United States" className="tr-input" />
            </div>
          </div>

          {/* ── Trader profile ── */}
          <div style={sectionLabel}>Trader profile</div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label className="tr-label">Market {req}</label>
              <select value={market} onChange={(e) => setMarket(e.target.value)} className="tr-input">
                <option value="forex">Forex</option>
                <option value="crypto">Crypto</option>
                <option value="stocks">Stocks</option>
                <option value="commodities">Commodities</option>
                <option value="indices">Indices</option>
                <option value="options">Options</option>
              </select>
            </div>
            <div>
              <label className="tr-label">Risk level {req}</label>
              <select value={riskLevel} onChange={(e) => setRiskLevel(e.target.value)} className="tr-input">
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label className="tr-label">Fee type {opt}</label>
              <select value={feeType} onChange={(e) => setFeeType(e.target.value)} className="tr-input">
                <option value="percentage">Percentage</option>
                <option value="flat">Flat</option>
                <option value="performance">Performance</option>
                <option value="none">None</option>
              </select>
            </div>
            <div>
              <label className="tr-label">Fee structure {opt}</label>
              <input type="text" value={feeStructure} onChange={(e) => setFeeStructure(e.target.value)}
                placeholder="e.g. 20% profit share" className="tr-input" />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label className="tr-label">Min capital ($) {opt}</label>
              <input type="number" min="0" value={minCapital} onChange={(e) => setMinCapital(e.target.value)}
                placeholder="e.g. 500" className="tr-input" />
            </div>
            <div>
              <label className="tr-label">Years active {opt}</label>
              <input type="number" min="0" max="50" value={yearsActive} onChange={(e) => setYearsActive(e.target.value)}
                placeholder="e.g. 3" className="tr-input" />
            </div>
          </div>

          {/* ── Bio & strategy ── */}
          <div style={sectionLabel}>About</div>

          <div>
            <label className="tr-label">Bio {opt}</label>
            <textarea rows={2} value={bio} onChange={(e) => setBio(e.target.value)}
              placeholder="Short trader bio…" className="tr-input" />
          </div>

          <div>
            <label className="tr-label">Strategy description {opt}</label>
            <textarea rows={2} value={strategyDesc} onChange={(e) => setStrategyDesc(e.target.value)}
              placeholder="e.g. Swing trading with price action setups on H4…" className="tr-input" />
          </div>

          <div className="tr-toggle-row">
            <button className={`tr-toggle ${isVerified ? "on" : "off"}`} onClick={() => setIsVerified((v) => !v)}>
              <div className="tr-toggle-thumb" />
            </button>
            <span className="tr-toggle-label">Mark as verified trader</span>
          </div>

          {error && <div className="tr-error"><AlertCircle size={13} style={{ flexShrink: 0 }} />{error}</div>}
        </div>

        <div className="tr-modal-foot">
          <button className="tr-ghost-btn" onClick={onClose}>Cancel</button>
          <button className="tr-submit-btn" disabled={loading} onClick={handleSubmit}>
            <Plus size={12} />{loading ? "Creating…" : "Create trader"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Confirm delete modal ─────────────────────────────────────────────────────
function ConfirmModal({ message, onConfirm, onClose, loading }) {
  return (
    <div className="tr-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="tr-modal tr-modal-sm">
        <div className="tr-modal-body" style={{ gap: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div className="tr-confirm-icon"><Trash2 size={16} style={{ color: "var(--rose)" }} /></div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text-primary)", marginBottom: 4 }}>Confirm removal</div>
              <div style={{ fontSize: 12.5, color: "var(--text-secondary)", lineHeight: 1.5 }}>{message}</div>
            </div>
          </div>
        </div>
        <div className="tr-modal-foot">
          <button className="tr-ghost-btn" onClick={onClose}>Cancel</button>
          <button className="tr-danger-btn" disabled={loading} onClick={onConfirm}>
            <Trash2 size={12} />{loading ? "Removing…" : "Remove"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Request row ──────────────────────────────────────────────────────────────
function RequestRow({ req, onAssign, onCancel, actionLoading }) {
  const client     = req.client;
  const traderUser = req.traderUser;
  const isOpen     = req.status === "open";
  const aColor     = avatarColor(req.id ?? 0);

  return (
    <div className="tr-req-row">
      <div className={`tr-avatar tr-avatar-${aColor}`}>
        {initials(client?.display_name)}
      </div>
      <div className="tr-req-body">
        <div className="tr-req-names">
          <span className="tr-req-client">{client?.display_name}</span>
          <span className="tr-req-verb">wants to hire</span>
          {traderUser
            ? <span className="tr-req-trader">{traderUser.display_name}</span>
            : <span className="tr-req-unassigned"><AlertCircle size={10} />No trader assigned</span>}
        </div>
        <div className="tr-req-criteria">
          <span>{fmtAmount(req.capital_amount)} {req.currency}</span>
          <span className="tr-req-dot">·</span>
          <span>{req.specialty}</span>
          <span className="tr-req-dot">·</span>
          <span>{req.risk_appetite} risk</span>
          <span className="tr-req-dot">·</span>
          <span>{req.fee_preference} fee</span>
        </div>
        {req.notes && <div className="tr-req-msg">{req.notes}</div>}
        <div className="tr-req-date">{fmtDateTime(req.created_at)}</div>
      </div>
      <div className="tr-req-actions">
        <StatusChip status={req.status} />
        {isOpen && (
          <>
            <button className="tr-assign-inline" disabled={actionLoading} onClick={onAssign}>
              <UserPlus size={11} />Assign trader
            </button>
            <button className="tr-reject-inline" disabled={actionLoading} onClick={onCancel}>
              <XCircle size={11} />Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Assign trader modal ───────────────────────────────────────────────────────
function AssignTraderModal({ req, traders, onClose, onSuccess }) {
  const [selectedId,      setSelectedId]      = useState(null);
  const [search,          setSearch]          = useState("");
  const [durationMonths,  setDurationMonths]  = useState("");
  const [feeStructure,    setFeeStructure]    = useState(req.fee_preference ?? "");
  const [saving,          setSaving]          = useState(false);
  const [err,             setErr]             = useState("");

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return traders;
    return traders.filter((t) =>
      (t.display_name ?? "").toLowerCase().includes(q) ||
      (t.handle ?? "").toLowerCase().includes(q) ||
      (t.strategy ?? "").toLowerCase().includes(q)
    );
  }, [traders, search]);

  async function handleSave() {
    if (!selectedId) { setErr("Select a trader to assign."); return; }
    setSaving(true);
    setErr("");
    try {
      // Create the agreement linking client + trader + the original request.
      const { error: agreementError } = await supabase.from("hire_agreements").insert({
        request_id:      req.id,
        client_id:       req.client_id,
        trader_id:       selectedId,
        capital_amount:  req.capital_amount,
        currency:        req.currency,
        duration_months: durationMonths ? Number(durationMonths) : null,
        fee_structure:   feeStructure || null,
        status:          "active",
      });
      if (agreementError) throw agreementError;

      // Close out the request now that it's been fulfilled.
      const { error: reqError } = await supabase
        .from("hire_requests")
        .update({ status: "fulfilled" })
        .eq("id", req.id);
      if (reqError) throw reqError;

      const trader = traders.find((t) => t.id === selectedId);
      onSuccess(trader);
    } catch (e) {
      setErr(e.message ?? "Failed to assign trader.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="tr-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="tr-modal" style={{ maxWidth: 460 }}>
        <div className="tr-modal-head">
          <div>
            <div className="tr-modal-title">Assign trader</div>
            <div className="tr-modal-sub">For {req.client?.display_name ?? "this client"}'s hire request</div>
          </div>
          <button className="tr-close-btn" onClick={onClose}><X size={13} /></button>
        </div>

        <div className="tr-modal-body">
          <div className="tr-req-criteria" style={{ background: "var(--bg-elevated)", padding: "10px 12px", borderRadius: "var(--radius-sm)", border: "1px solid var(--border)" }}>
            <span>{fmtAmount(req.capital_amount)} {req.currency}</span>
            <span className="tr-req-dot">·</span>
            <span>{req.specialty}</span>
            <span className="tr-req-dot">·</span>
            <span>{req.risk_appetite} risk</span>
            <span className="tr-req-dot">·</span>
            <span>{req.fee_preference} fee</span>
          </div>

          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search traders by name, handle, or strategy…"
            className="tr-input"
          />

          {err && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "var(--rose)" }}>
              <AlertCircle size={12} />{err}
            </div>
          )}

          <div className="tr-assign-list">
            {filtered.length === 0 ? (
              <div className="tr-empty"><AlertCircle size={20} /><span>No traders match your search</span></div>
            ) : filtered.map((t) => {
              const aColor = avatarColor(t.id);
              const isSelected = selectedId === t.id;
              return (
                <div
                  key={t.id}
                  className={`tr-assign-option${isSelected ? " selected" : ""}`}
                  onClick={() => setSelectedId(t.id)}
                >
                  <div className={`tr-avatar tr-avatar-${aColor}`} style={{ width: 30, height: 30, fontSize: 11 }}>
                    {initials(t.display_name)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text-primary)" }}>{t.display_name}</div>
                    <div style={{ fontSize: 10.5, color: "var(--text-muted)" }}>
                      {t.strategy ?? "No strategy listed"}
                    </div>
                  </div>
                  {t.verified && <ShieldCheck size={13} style={{ color: "var(--green)", flexShrink: 0 }} />}
                  {isSelected && <CheckCircle2 size={15} style={{ color: "var(--accent-light)", flexShrink: 0 }} />}
                </div>
              );
            })}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={{ fontSize: 10.5, color: "var(--text-muted)", display: "block", marginBottom: 5 }}>Duration (months)</label>
              <input
                value={durationMonths}
                onChange={(e) => setDurationMonths(e.target.value)}
                placeholder="e.g. 6"
                type="number"
                min="1"
                className="tr-input"
              />
            </div>
            <div>
              <label style={{ fontSize: 10.5, color: "var(--text-muted)", display: "block", marginBottom: 5 }}>Fee structure</label>
              <input
                value={feeStructure}
                onChange={(e) => setFeeStructure(e.target.value)}
                placeholder="e.g. 20% profit share"
                className="tr-input"
              />
            </div>
          </div>
        </div>

        <div className="tr-modal-foot">
          <button className="tr-ghost-btn" onClick={onClose}>Cancel</button>
          <button className="tr-btn-primary" disabled={saving || !selectedId} onClick={handleSave}>
            {saving ? "Assigning…" : "Assign trader"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function Traders() {
  const navigate = useNavigate();

  const [adminEmail,    setAdminEmail]    = useState("");
  const [adminInitials, setAdminInitials] = useState("A");
  const [sessionReady,  setSessionReady]  = useState(false);

  const [tab,           setTab]           = useState("traders");
  const [traders,       setTraders]       = useState([]);
  const [requests,      setRequests]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [reqLoading,    setReqLoading]    = useState(true);
  const [search,        setSearch]        = useState("");
  const [statusFilter,  setStatusFilter]  = useState("");
  const [sortField,     setSortField]     = useState("created_at");
  const [sortDir,       setSortDir]       = useState(-1);
  const [page,          setPage]          = useState(1);
  const [showAdd,       setShowAdd]       = useState(false);
  const [viewTrader,    setViewTrader]    = useState(null);
  const [deleteTarget,  setDeleteTarget]  = useState(null);
  const [deleting,      setDeleting]      = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [assignTarget,  setAssignTarget]  = useState(null);
  const [toast,         setToast]         = useState({ msg: "", ok: true });

  // ── Mobile sidebar ───────────────────────────────────────────────────────
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const closeSidebar = () => setSidebarOpen(false);
  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [sidebarOpen]);
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) { navigate("/admin/login", { replace: true }); return; }
      const email = session.user?.email ?? "";
      setAdminEmail(email);
      const parts = email.split("@")[0].split(".");
      setAdminInitials((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? parts[0]?.[1] ?? ""));
      setSessionReady(true);
    });
  }, [navigate]);

  async function handleLogout() {
    await supabase.auth.signOut();
    navigate("/admin/login", { replace: true });
  }

  // ── Fetch traders ───────────────────────────────────────────────────────
  const fetchTraders = useCallback(async () => {
    setLoading(true);
    try {
      // Traders are standalone — personal info lives on trader_profiles, no users join needed
      const { data: profileRows, error } = await supabase
        .from("trader_profiles")
        .select("id, user_id, handle, display_name, market, risk_level, strategy_desc, min_capital, fee_structure, fee_type, is_verified, is_active, years_active, total_copiers, total_clients, badge_label, badge_type, color_hex, initials, bio, strategy, email, phone, country, status, verified, created_at")
        .order("created_at", { ascending: false });
      if (error) throw error;
      // Normalise shape so the rest of the UI can use t.display_name directly
      setTraders(profileRows ?? []);
    } catch (e) {
      console.error("[Traders] fetch error:", e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Fetch hire requests ─────────────────────────────────────────────────
  const fetchRequests = useCallback(async () => {
    setReqLoading(true);
    try {
      const { data: reqRows, error } = await supabase
        .from("hire_requests")
        .select("id, client_id, capital_amount, currency, specialty, risk_appetite, fee_preference, max_monthly_budget, notes, status, created_at")
        .order("created_at", { ascending: false });
      if (error) throw error;

      // Pull any agreements tied to these requests, so we know which requests
      // already have a trader assigned (and who).
      const requestIds = (reqRows ?? []).map((r) => r.id);
      const clientIds  = [...new Set((reqRows ?? []).map((r) => r.client_id).filter(Boolean))];

      const [{ data: clientRows }, { data: agreementRows }] = await Promise.all([
        clientIds.length
          ? supabase.from("users").select("id, display_name, email").in("id", clientIds)
          : Promise.resolve({ data: [] }),
        requestIds.length
          ? supabase.from("hire_agreements").select("id, request_id, client_id, trader_id, status, started_at").in("request_id", requestIds)
          : Promise.resolve({ data: [] }),
      ]);

      const traderIds = [...new Set((agreementRows ?? []).map((a) => a.trader_id).filter(Boolean))];
      const { data: traderRows } = traderIds.length
        ? await supabase.from("trader_profiles").select("id, display_name, handle, strategy, verified").in("id", traderIds)
        : { data: [] };

      const clientMap = {};
      (clientRows ?? []).forEach((u) => { clientMap[u.id] = u; });
      const traderMap = {};
      (traderRows ?? []).forEach((t) => { traderMap[t.id] = t; });
      const agreementByRequest = {};
      (agreementRows ?? []).forEach((a) => { agreementByRequest[a.request_id] = a; });

      setRequests((reqRows ?? []).map((r) => {
        const agreement = agreementByRequest[r.id] ?? null;
        return {
          ...r,
          client:     clientMap[r.client_id] ?? null,
          agreement,
          traderUser: agreement ? (traderMap[agreement.trader_id] ?? null) : null,
        };
      }));
    } catch (e) {
      console.error("[Requests] fetch error:", e.message);
    } finally {
      setReqLoading(false);
    }
  }, []);

  const fetchAll = useCallback(async () => {
    await Promise.all([fetchTraders(), fetchRequests()]);
  }, [fetchTraders, fetchRequests]);

  useEffect(() => { if (sessionReady) fetchAll(); }, [sessionReady, fetchAll]);

  function showToast(msg, ok = true) {
    setToast({ msg, ok });
    setTimeout(() => setToast({ msg: "", ok: true }), 3200);
  }

  // ── Assign trader to request (creates hire_agreements row, marks request fulfilled) ──
  function handleAssignSuccess(trader) {
    setRequests((prev) => prev.map((r) =>
      r.id === assignTarget.id ? { ...r, status: "fulfilled", traderUser: trader ?? null } : r
    ));
    setAssignTarget(null);
    showToast(`${trader?.display_name ?? "Trader"} assigned to request.`);
  }

  // ── Cancel hire request ─────────────────────────────────────────────────
  async function handleCancelRequest(req) {
    setActionLoading(req.id);
    try {
      const { error } = await supabase.from("hire_requests").update({ status: "cancelled" }).eq("id", req.id);
      if (error) throw error;
      showToast("Hire request cancelled.");
      setRequests((prev) => prev.map((r) => r.id === req.id ? { ...r, status: "cancelled" } : r));
    } catch (e) {
      showToast(e.message ?? "Failed to cancel.", false);
    } finally {
      setActionLoading(null);
    }
  }

  // ── Delete trader ───────────────────────────────────────────────────────
  async function handleDeleteTrader() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const { error } = await supabase.from("trader_profiles").delete().eq("id", deleteTarget.id);
      if (error) throw error;
      showToast("Trader profile removed.");
      setDeleteTarget(null);
      await fetchTraders();
    } catch (e) {
      showToast(e.message ?? "Failed to remove.", false);
    } finally {
      setDeleting(false);
    }
  }

  // ── Stats ───────────────────────────────────────────────────────────────
  const stats = useMemo(() => ({
    total:    traders.length,
    verified: traders.filter((t) => t.verified).length,
    active:   traders.filter((t) => t.status === "active").length,
    pending:  requests.filter((r) => r.status === "open").length,
  }), [traders, requests]);

  const STAT_CARDS = [
    { label: "Total traders",     value: stats.total,    icon: UserCheck,  bg: "rgba(99,102,241,0.15)",  color: "var(--accent-light)", glow: "rgba(99,102,241,0.08)",  bar: "var(--accent)",  sub: "All profiles" },
    { label: "Verified",          value: stats.verified, icon: ShieldCheck,bg: "rgba(52,211,153,0.15)",  color: "var(--green)",        glow: "rgba(52,211,153,0.08)",  bar: "var(--green)",   sub: "Verified traders" },
    { label: "Active",            value: stats.active,   icon: TrendingUp, bg: "rgba(34,211,238,0.15)",  color: "var(--cyan)",         glow: "rgba(34,211,238,0.08)",  bar: "var(--cyan)",    sub: "Currently active" },
    { label: "Open requests",     value: stats.pending,  icon: Clock,      bg: "rgba(251,191,36,0.15)",  color: "var(--amber)",        glow: "rgba(251,191,36,0.08)",  bar: "var(--amber)",   sub: "Awaiting assignment" },
  ];

  // ── Filter + sort traders ───────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    let list = traders.filter((t) => {
      const name = (t.display_name ?? "").toLowerCase();
      const matchSearch = !q || name.includes(q) || (t.email ?? "").toLowerCase().includes(q);
      const matchStatus = !statusFilter || t.status === statusFilter;
      return matchSearch && matchStatus;
    });
    list.sort((a, b) => {
      if (sortField === "name") {
        const an = a.display_name ?? "";
        const bn = b.display_name ?? "";
        return an.localeCompare(bn) * sortDir;
      }
      return (new Date(a.created_at ?? 0) - new Date(b.created_at ?? 0)) * sortDir;
    });
    return list;
  }, [traders, search, statusFilter, sortField, sortDir]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const pageSlice  = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  function handleSort(field) {
    if (field === sortField) setSortDir((d) => d * -1);
    else { setSortField(field); setSortDir(-1); }
    setPage(1);
  }

  const pendingReqs = requests.filter((r) => r.status === "open");
  const otherReqs   = requests.filter((r) => r.status !== "open");

  if (!sessionReady) {
    return (
      <div className="tr-gate">
        <StyleInjector />
        <div className="tr-gate-icon"><UserCheck size={16} color="#fff" /></div>
        <p style={{ fontSize: 12, color: "var(--text-muted)", fontFamily: "Inter, sans-serif" }}>Verifying session…</p>
      </div>
    );
  }

  return (
    <div className="tr-shell">
      <StyleInjector />

      {/* Mobile overlay */}
      <div className={`tr-overlay-mob${sidebarOpen ? " open" : ""}`} onClick={closeSidebar} aria-hidden="true" />

      {/* ── Sidebar ── */}
      <aside className={`ad-sidebar${sidebarOpen ? " open" : ""}`}>
        <div className="ad-logo">
          <div className="ad-logo-icon"><CandlestickChart size={16} color="#fff" /></div>
          <div className="ad-logo-text">
            <strong>Tradexflow</strong>
            <span>Admin panel</span>
          </div>
          <button className="ad-logo-close" onClick={closeSidebar}><X size={14} /></button>
        </div>

        <nav className="ad-nav">
          {NAV_SECTIONS.map((section) => (
            <div key={section.label}>
              <p className="ad-nav-section-label">{section.label}</p>
              <div className="ad-nav-items">
                {section.items.map((item) => <SidebarLink key={item.to} {...item} onClick={closeSidebar} />)}
              </div>
            </div>
          ))}
        </nav>

        <div className="ad-user-card">
          <div className="ad-user-inner">
            <div className="ad-user-avatar">{adminInitials.toUpperCase()}</div>
            <div className="ad-user-info">
              <strong>Super Admin</strong>
              <span>{adminEmail}</span>
            </div>
            <button className="ad-logout-btn" onClick={handleLogout} title="Logout">
              <LogOut size={13} />
            </button>
          </div>
        </div>
      </aside>

      {/* ── Page ── */}
      <div className="tr-page">

        {/* Topbar */}
        <header className="tr-topbar">
          <div className="tr-topbar-left">
            <button className="tr-hamburger" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
              <Menu size={16} />
            </button>
            <div className="tr-topbar-badge"><UserCheck size={9} />Traders</div>
            <div>
              <div className="tr-topbar-title">Trader management</div>
              <div className="tr-topbar-sub">Manage trader profiles and hire requests</div>
            </div>
          </div>
          <div className="tr-topbar-right">
            <button className="tr-btn" onClick={fetchAll}><RefreshCw size={12} />Refresh</button>
            <button className="tr-btn-primary" onClick={() => setShowAdd(true)}><Plus size={12} />Add trader</button>
          </div>
        </header>

        <main className="tr-main">

          {/* Stat cards */}
          <div className="tr-stat-grid">
            {STAT_CARDS.map(({ label, value, icon: Icon, bg, color, glow, bar, sub }) => (
              <div key={label} className="tr-stat" style={{ "--stat-glow": glow, "--stat-bar": bar }}>
                <div className="tr-stat-top">
                  <span className="tr-stat-label">{label}</span>
                  <div className="tr-stat-icon" style={{ background: bg }}>
                    <Icon size={14} style={{ color }} />
                  </div>
                </div>
                <div>
                  <div className="tr-stat-value">{value}</div>
                  {sub && <div className="tr-stat-sub">{sub}</div>}
                </div>
              </div>
            ))}
          </div>

          {/* Pending hire requests banner */}
          {stats.pending > 0 && (
            <div className="tr-pending-banner">
              <Clock size={14} style={{ color: "var(--amber)", flexShrink: 0 }} />
              <p>
                <strong>{stats.pending} hire request{stats.pending > 1 ? "s" : ""}</strong> awaiting approval.
              </p>
              <button className="tr-pending-link" onClick={() => setTab("requests")}>
                Review now →
              </button>
            </div>
          )}

          {/* Tabs */}
          <div className="tr-tabs">
            {[
              { key: "traders",  label: "Trader profiles", count: traders.length,  alert: false },
              { key: "requests", label: "Hire requests",   count: stats.pending,   alert: stats.pending > 0 },
            ].map(({ key, label, count, alert }) => (
              <button key={key} className={`tr-tab${tab === key ? " active" : ""}`} onClick={() => setTab(key)}>
                {label}
                <span className={`tr-tab-badge ${alert ? "alert" : "normal"}`}>{count}</span>
              </button>
            ))}
          </div>

          {/* ── TRADERS TAB ─────────────────────────────────────────────────── */}
          {tab === "traders" && (
            <div className="tr-panel">
              {/* Toolbar */}
              <div className="tr-toolbar">
                <div className="tr-toolbar-left">
                  <span className="tr-panel-title">All traders</span>
                  <span className="tr-count-pill">{filtered.length}</span>
                </div>
                <div className="tr-toolbar-right">
                  <Filter size={12} style={{ color: "var(--text-muted)" }} />
                  <select className="tr-select" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
                    <option value="">All statuses</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  <div className="tr-search-wrap">
                    <Search size={12} className="tr-search-icon" />
                    <input
                      type="text" placeholder="Search traders…"
                      value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                      className="tr-search"
                    />
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="tr-table-wrap">
                <table className="tr-table">
                  <thead>
                    <tr>
                      {[
                        { key: "name",       label: "Trader" },
                        { key: "created_at", label: "Joined" },
                      ].map(({ key, label }) => (
                        <th key={key} onClick={() => handleSort(key)}>
                          {label}<SortIcon field={key} sortField={sortField} sortDir={sortDir} />
                        </th>
                      ))}
                      <th className="no-sort">Status</th>
                      <th className="no-sort">Verified</th>
                      <th className="no-sort">Strategy</th>
                      <th className="no-sort" style={{ textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      Array.from({ length: 8 }).map((_, i) => (
                        <tr key={i}>
                          {Array.from({ length: 6 }).map((_, j) => (
                            <td key={j}><div className="tr-skel" style={{ width: `${40 + (i * 13 + j * 7) % 45}%` }} /></td>
                          ))}
                        </tr>
                      ))
                    ) : pageSlice.length === 0 ? (
                      <tr>
                        <td colSpan={6}>
                          <div className="tr-empty"><AlertCircle size={22} /><span>No traders match your filters</span></div>
                        </td>
                      </tr>
                    ) : pageSlice.map((t) => {
                      const aColor = avatarColor(t.id);
                      return (
                        <tr key={t.id}>
                          <td>
                            <div className="tr-avatar-cell">
                              <div className={`tr-avatar tr-avatar-${aColor}`}>
                                {initials(t.display_name)}
                              </div>
                              <div>
                                <div className="tr-cell-name">{t.display_name}</div>
                                <div className="tr-cell-email">{t.email}</div>
                              </div>
                            </div>
                          </td>
                          <td><span className="tr-date">{fmtDate(t.created_at)}</span></td>
                          <td><StatusChip status={t.status ?? "inactive"} /></td>
                          <td>
                            {t.verified
                              ? <span className="tr-verified"><ShieldCheck size={9} />Verified</span>
                              : <span style={{ fontSize: 11, color: "var(--text-muted)" }}>—</span>}
                          </td>
                          <td>
                            <div className="tr-strategy">{t.strategy ?? <span style={{ color: "var(--text-muted)" }}>—</span>}</div>
                          </td>
                          <td>
                            <div className="tr-actions">
                              <button className="tr-icon-btn" onClick={() => setViewTrader(t)} title="View profile"><Eye size={12} /></button>
                              <button className="tr-icon-btn danger" onClick={() => setDeleteTarget(t)} title="Remove trader"><Trash2 size={12} /></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="tr-pagination">
                  <span className="tr-pag-info">
                    {filtered.length
                      ? `${(page - 1) * PER_PAGE + 1}–${Math.min(page * PER_PAGE, filtered.length)} of ${filtered.length}`
                      : "0 results"}
                  </span>
                  <div className="tr-pag-pages">
                    {Array.from({ length: Math.min(totalPages, 8) }, (_, i) => i + 1).map((p) => (
                      <button key={p} className={`tr-pag-btn${page === p ? " active" : ""}`} onClick={() => setPage(p)}>{p}</button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── HIRE REQUESTS TAB ──────────────────────────────────────────── */}
          {tab === "requests" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

              {/* Pending */}
              {pendingReqs.length > 0 && (
                <div className="tr-panel-amber">
                  <div className="tr-toolbar" style={{ borderColor: "rgba(251,191,36,0.15)" }}>
                    <div className="tr-toolbar-left">
                      <Clock size={13} style={{ color: "var(--amber)" }} />
                      <span className="tr-panel-title" style={{ color: "var(--amber)" }}>Open requests</span>
                      <span style={{ padding: "1px 8px", borderRadius: 99, background: "var(--amber)", color: "#0a0a0a", fontSize: 10, fontWeight: 700 }}>{pendingReqs.length}</span>
                    </div>
                  </div>
                  {pendingReqs.map((r) => (
                    <RequestRow key={r.id} req={r}
                      onAssign={() => setAssignTarget(r)}
                      onCancel={() => handleCancelRequest(r)}
                      actionLoading={actionLoading === r.id}
                    />
                  ))}
                </div>
              )}

              {/* All requests */}
              <div className="tr-panel">
                <div className="tr-toolbar">
                  <div className="tr-toolbar-left">
                    <span className="tr-panel-title">All requests</span>
                    <span className="tr-count-pill">{requests.length}</span>
                  </div>
                </div>
                {reqLoading ? (
                  <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
                    {[1,2,3,4].map((i) => <div key={i} className="tr-skel" style={{ height: 54 }} />)}
                  </div>
                ) : requests.length === 0 ? (
                  <div className="tr-empty"><AlertCircle size={22} /><span>No hire requests yet</span></div>
                ) : (
                  [...pendingReqs, ...otherReqs].map((r) => (
                    <RequestRow key={r.id} req={r}
                      onAssign={() => setAssignTarget(r)}
                      onCancel={() => handleCancelRequest(r)}
                      actionLoading={actionLoading === r.id}
                    />
                  ))
                )}
              </div>
            </div>
          )}

        </main>
      </div>

      {/* ── Modals ── */}
      {showAdd && (
        <AddTraderModal
          onClose={() => setShowAdd(false)}
          onSuccess={(msg) => { showToast(msg); fetchTraders(); }}
        />
      )}
      {viewTrader && (
        <TraderDetailModal
          trader={viewTrader}
          onClose={() => setViewTrader(null)}
          onDelete={(t) => { setViewTrader(null); setDeleteTarget(t); }}
        />
      )}
      {deleteTarget && (
        <ConfirmModal
          message={`Remove trader profile for ${deleteTarget.display_name}? This action cannot be undone.`}
          onConfirm={handleDeleteTrader}
          onClose={() => setDeleteTarget(null)}
          loading={deleting}
        />
      )}
      {assignTarget && (
        <AssignTraderModal
          req={assignTarget}
          traders={traders}
          onClose={() => setAssignTarget(null)}
          onSuccess={handleAssignSuccess}
        />
      )}

      {/* Toast */}
      {toast.msg && (
        <div className={`tr-toast ${toast.ok ? "ok" : "err"}`}>
          {toast.ok
            ? <CheckCircle2 size={14} style={{ color: "var(--green)", flexShrink: 0 }} />
            : <XCircle      size={14} style={{ color: "var(--rose)",  flexShrink: 0 }} />}
          {toast.msg}
        </div>
      )}
    </div>
  );
}